import { useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { createOrganization, OrgAdminApiError } from '@/api/orgAdmin';
import { Modal, useToast } from '@/components/ui';
import { ORG_CODE_PATTERN, slugifyHint } from './orgFormat';

interface CreateOrganizationModalProps {
    open: boolean;
    onClose: () => void;
}

function serverCodeError(error: unknown): string | null {
    if (!(error instanceof OrgAdminApiError)) return null;
    const details = error.details as Record<string, unknown> | undefined;
    const codeErrors = details?.code;
    if (Array.isArray(codeErrors) && typeof codeErrors[0] === 'string') {
        return codeErrors[0];
    }
    return null;
}

/**
 * EAA-1815: "Tạo tổ chức" — frame 23 (all 114 lines), the create-org modal.
 *
 * Two fields only (name, code) — the frame has no branding/color field on
 * this screen (branding is set later, in the detail page's own editor); the
 * previous version of this form carried a "Màu thương hiệu" input that no
 * design frame shows at create time, so it is dropped here.
 *
 * Code rules straight from the frame: `^[a-z0-9-]+$`, validated on blur (not
 * on every keystroke), immutability stated under the field permanently — not
 * only when something is wrong — and a duplicate code from the server lands
 * as this SAME inline message, never a toast alone (rule: "Duplicate code =
 * inline field error, never a toast alone"). Submit stays disabled until the
 * code is syntactically valid; the frame's own disabled-button state (line
 * 103) is what an invalid code looks like before submission is even
 * attempted.
 */
export function CreateOrganizationModal({ open, onClose }: CreateOrganizationModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [codeTouched, setCodeTouched] = useState(false);
    const [duplicateError, setDuplicateError] = useState<string | null>(null);

    const codeFormatValid = ORG_CODE_PATTERN.test(code);
    const showFormatError = codeTouched && code.length > 0 && !codeFormatValid;
    const codeInvalid = showFormatError || Boolean(duplicateError);
    const canSubmit = name.trim().length > 0 && code.length > 0 && codeFormatValid;

    const reset = () => {
        setName('');
        setCode('');
        setCodeTouched(false);
        setDuplicateError(null);
    };

    const createMutation = useMutation({
        mutationFn: () =>
            createOrganization(config.apiBase, accessToken as string, {
                code: code.trim(),
                name: name.trim(),
            }),
        onSuccess: (data) => {
            reset();
            onClose();
            toast.success(t('admin.organizations.createModal.toastCreated', { name: data.name }));
            void queryClient.invalidateQueries({ queryKey: ['organizations-list'] });
            void queryClient.invalidateQueries({ queryKey: ['home-orgs-count'] });
        },
        onError: (error) => {
            const dup = serverCodeError(error);
            if (dup) {
                setDuplicateError(dup);
                return;
            }
            toast.error(
                error instanceof OrgAdminApiError
                    ? error.message
                    : t('admin.organizations.createModal.genericCreateError')
            );
        },
    });

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        setCodeTouched(true);
        if (!canSubmit) return;
        createMutation.mutate();
    };

    const handleClose = () => {
        if (createMutation.isPending) return;
        reset();
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={t('admin.organizations.createOrgAction')}
            width={520}
            footer={
                <>
                    <button
                        type="button"
                        className="portal-btn"
                        onClick={handleClose}
                        disabled={createMutation.isPending}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="submit"
                        form="create-org-form"
                        className="portal-btn portal-btn-primary"
                        disabled={!canSubmit || createMutation.isPending}
                    >
                        {createMutation.isPending
                            ? t('admin.organizations.createModal.submitPending')
                            : t('admin.organizations.createOrgAction')}
                    </button>
                </>
            }
        >
            <form id="create-org-form" className="portal-orgs-form" onSubmit={onSubmit}>
                <label>
                    <span className="portal-orgs-form-label">
                        {t('admin.organizations.createModal.nameLabel')}
                    </span>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('admin.organizations.createModal.namePlaceholder')}
                        required
                        autoFocus
                    />
                </label>
                <label>
                    <span className="portal-orgs-form-label">
                        {t('admin.organizations.createModal.codeLabel')}
                    </span>
                    <input
                        className={
                            'portal-orgs-code-input' + (codeInvalid ? ' is-invalid' : '')
                        }
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value);
                            setDuplicateError(null);
                        }}
                        onBlur={() => setCodeTouched(true)}
                        placeholder={t('admin.organizations.createModal.codePlaceholder')}
                        required
                    />
                    <span className={'portal-orgs-code-hint' + (codeInvalid ? ' is-invalid' : '')}>
                        {duplicateError ? (
                            duplicateError
                        ) : (
                            <Trans
                                i18nKey="admin.organizations.createModal.codeHint"
                                values={{ example: slugifyHint(name) || 'tan-an' }}
                                components={{ code: <code /> }}
                            />
                        )}
                    </span>
                </label>

                <span className="portal-orgs-aftercreate">
                    <span className="portal-orgs-aftercreate-title">
                        {t('admin.organizations.createModal.afterCreateTitle')}
                    </span>
                    <ul>
                        <li>{t('admin.organizations.createModal.afterCreateLevels')}</li>
                        <li>{t('admin.organizations.createModal.afterCreateLinks')}</li>
                        <li>{t('admin.organizations.createModal.afterCreateTenants')}</li>
                    </ul>
                </span>
            </form>
        </Modal>
    );
}

export default CreateOrganizationModal;
